import { GAEventName } from 'analytics/ga-event-name.enum';
import { StoreCode } from 'schema/storeCode';
import { ProductResponseItem } from 'state/internal/getProducts/types';
import { SubscriptionItem } from 'state/upscribe/getSubscriptionDetail/types';
import { createAnalyticsEvent } from '../create-analytics-event';
import { CheckoutType } from './ecommerce';

type ProductItemProps = {
  id: string;
  product_id: number;
  quantity: number;
};

export enum ProductCategoryEventEnum {
  OneTime = 'One Time',
  Subscription = 'Subscription'
}
export enum ProductCartEventEnum {
  NextOrder = 'Next Order',
  Subscription = 'Subscription'
}
export enum CheckoutStepOptionEnum {
  ShippingMethod = 'Shipping Method',
  ShippingAddress = 'Shipping Address'
}
export type ProductFullEventType = {
  productId: number | string;
  name: string;
  category: ProductCategoryEventEnum;
  variant?: string;
  variantId: number | string;
  quantity: number;
  price: number | string;
};

const prepareProduct = (product: ProductFullEventType) => ({
  product_id: product.productId,
  product_name: product.name,
  product_category: product.category,
  product_variant: product?.variant,
  product_variant_id: product.variantId,
  product_quantity: product.quantity,
  product_price: product.price
});

export const activateSubscription = createAnalyticsEvent<{
  subscriptionId: number;
  customerId?: number;
  store: StoreCode;
}>(({ subscriptionId, customerId, store }) => ({
  event: GAEventName.SUBSCRIPTION_REACTIVATE,
  subscription_id: subscriptionId,
  customerId,
  store
}));

export const viewSubscription = createAnalyticsEvent<{
  subscriptionId: string;
}>(({ subscriptionId }) => ({
  event: GAEventName.VIEW_SUBSCRIPTION,
  subscriptionId
}));

export const addToSubscriptionEvent = createAnalyticsEvent<{
  subscriptionId: number;
  items: ProductItemProps[];
  store: StoreCode;
}>(({ subscriptionId, items, store }) => ({
  event: GAEventName.ADD_TO_SUBSCRIPTION,
  subscriptionId,
  items,
  store
}));

export const addToNextOrderViaModalWindowEvent = createAnalyticsEvent<{
  subscriptionId: number;
  queueId: number;
  items: ProductItemProps[];
  store: StoreCode;
}>(({ subscriptionId, queueId, items, store }) => ({
  event: GAEventName.ADD_TO_NEXT_ORDER_VIA_MODAL_WINDOW,
  subscriptionId,
  queueId,
  items,
  store
}));

export const addToSubscriptionViaModalWindowEvent = createAnalyticsEvent<{
  subscriptionId: number;
  items: ProductItemProps[];
  store: StoreCode;
}>(({ subscriptionId, items, store }) => ({
  event: GAEventName.ADD_TO_SUBSCRIPTION_VIA_MODAL_WINDOW,
  subscriptionId,
  items,
  store
}));

export const addPromocodeEvent = createAnalyticsEvent<{
  subscriptionId: number;
  promocode?: string;
  store: StoreCode;
}>(({ subscriptionId, promocode, store }) => ({
  event: GAEventName.ADD_PROMOCODE,
  subscriptionId,
  promocode,
  store
}));

export const removePromocodeEvent = createAnalyticsEvent<{
  subscriptionId: number;
  store: StoreCode;
}>(({ subscriptionId, store }) => ({
  event: GAEventName.REMOVE_PROMOCODE,
  subscriptionId,
  store
}));

// from \shopify-theme\upscribe-customer-portal\components\switch-modal.vue
export const switchPouchPacksEvent = createAnalyticsEvent<{
  type: 'switch' | 'undo';
  subscriptionId: number;
  queueId: number;
  fromLineItem: SubscriptionItem;
  toProduct: ProductResponseItem;
  toVariantId: number;
  switchQuantity: number;
  store: StoreCode;
}>(
  ({
    type,
    subscriptionId,
    queueId,
    fromLineItem,
    toProduct,
    toVariantId,
    switchQuantity,
    store
  }) => ({
    event:
      type === 'switch'
        ? GAEventName.SUBSCRIPTION_PRODUCT_SWITCH
        : GAEventName.SUBSCRIPTION_PRODUCT_SWITCH_UNDO,
    subscription_id: subscriptionId,
    queueId,
    fromLineItem,
    toProduct,
    toVariantId,
    switchQuantity,
    store
  })
);

export const clickAddTravelPacksBannerButtonEvent = createAnalyticsEvent<{
  subscriptionId?: number;
  page: 'details' | 'list';
}>(({ subscriptionId = null, page }) => {
  const event = {
    event: GAEventName.ADD_TRAVEL_PACKS_BANNER_BUTTON_CLICK
  };
  if (page === 'details' && subscriptionId) {
    return {
      ...event,
      subscriptionId
    };
  }
  return event;
});

export const subscriptionSkipClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number | null;
  checkoutType: CheckoutType;
  location: string;
  skipDurationDays: number;
  newDate: string;
  oldDate: string;
}>(
  ({ subscriptionId, customerId, checkoutType, location, skipDurationDays, newDate, oldDate }) => ({
    event: GAEventName.SUBSCRIPTION_SKIP,
    subscription_id: subscriptionId,
    customer_id: customerId,
    checkout_type: checkoutType,
    location,
    skip_duration_days: skipDurationDays,
    new_date: newDate,
    old_date: oldDate
  })
);

export const subscriptionDelayClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number | null;
  checkoutType: CheckoutType;
  location: string;
  newDate: string;
  oldDate: string | null;
}>(({ subscriptionId, customerId, checkoutType, location, newDate, oldDate }) => ({
  event: GAEventName.SUBSCRIPTION_DELAY,
  subscription_id: subscriptionId,
  customer_id: customerId,
  checkout_type: checkoutType,
  location,
  delay_days: 7,
  new_date: newDate,
  old_date: oldDate
}));

export const subscriptionCancelClickEvent = createAnalyticsEvent<{
  reason: string;
  checkoutType: CheckoutType;
  subscriptionId: number;
  customerId: number;
  store: StoreCode;
}>(({ reason, subscriptionId, customerId, store, checkoutType }) => ({
  event: GAEventName.SUBSCRIPTION_CANCEL,
  cancel_reason: reason,
  subscription_id: subscriptionId,
  customer_id: customerId,
  store,
  checkout_type: checkoutType
}));

export const subscriptionActivateClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number;
  store: StoreCode;
}>(({ subscriptionId, customerId, store }) => ({
  event: GAEventName.SUBSCRIPTION_REACTIVATE,
  subscription_id: subscriptionId,
  customer_id: customerId,
  store
}));

export const subscriptionShipClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  location: string;
}>(({ subscriptionId, checkoutType, location }) => ({
  event: GAEventName.SUBSCRIPTION_SHIP_NOW,
  subscriptionId,
  checkout_type: checkoutType,
  location
}));

export const subscriptionShipmentDateClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number | null;
  checkoutType: CheckoutType;
  location: string;
  dateDifference: number;
  newDate: string;
  oldDate: string;
}>(({ subscriptionId, customerId, checkoutType, dateDifference, newDate, oldDate, location }) => ({
  event: GAEventName.SUBSCRIPTION_SHIPMENT_DATE,
  subscription_id: subscriptionId,
  customer_id: customerId,
  checkout_type: checkoutType,
  date_difference: dateDifference,
  new_date: newDate,
  old_date: oldDate,
  location
}));

export const subscriptionSelectDateModalClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number | null;
  checkoutType: CheckoutType;
  nextOrderDate: string;
  location: string;
}>(({ subscriptionId, customerId, checkoutType, nextOrderDate, location }) => ({
  event: GAEventName.SUBSCRIPTION_NEXT_ORDER_DATE_OPENED,
  subscription_id: subscriptionId,
  customer_id: customerId,
  checkout_type: checkoutType,
  next_order_date: nextOrderDate,
  location
}));

export const subscriptionNextOrderDateClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  customerId: number | null;
  checkoutType: CheckoutType;
  numberOfDays: number;
  location: string;
  disabled: boolean;
}>(({ subscriptionId, customerId, checkoutType, numberOfDays, location, disabled }) => ({
  event: disabled
    ? GAEventName.SUBSCRIPTION_NEXT_ORDER_DATE_INVALID
    : GAEventName.SUBSCRIPTION_NEXT_ORDER_DATE_PUSHOUT,
  subscription_id: subscriptionId,
  customer_id: customerId,
  checkout_type: checkoutType,
  number_of_days: numberOfDays,
  location
}));

export const subscriptionShipmentFrequencyEvent = createAnalyticsEvent<{
  subscriptionId?: number;
  checkoutType: CheckoutType;
  location: string;
  shipFrequency: string;
}>(({ subscriptionId, checkoutType, shipFrequency, location }) => ({
  event: GAEventName.SUBSCRIPTION_SHIPMENT_FREQUENCY,
  subscription_id: subscriptionId,
  checkout_type: checkoutType,
  ship_frequency: shipFrequency,
  location
}));

export const subscriptionProductQuantityChangeClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  product: ProductFullEventType;
}>(({ subscriptionId, checkoutType, product }) => ({
  event: GAEventName.SUBSCRIPTION_PRODUCT_QUANTITY_CHANGE,
  subscription_id: subscriptionId,
  checkout_type: checkoutType,
  ...prepareProduct(product)
}));

export const subscriptionProductSwitchClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  product: ProductFullEventType;
}>(({ subscriptionId, checkoutType, product }) => ({
  event: GAEventName.SUBSCRIPTION_PRODUCT_SWITCH,
  subscriptionId,
  checkout_type: checkoutType,
  ...prepareProduct(product)
}));

export const subscriptionProductAddClickEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  addToCartType: ProductCartEventEnum;
  product: ProductFullEventType;
}>(({ subscriptionId, checkoutType, addToCartType, product }) => ({
  event: GAEventName.SUBSCRIPTION_PRODUCT_ADD,
  subscription_id: subscriptionId,
  checkout_type: checkoutType,
  add_to_cart_type: addToCartType,
  ...prepareProduct(product)
}));

export const subscriptionPaymentInfoEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  addToCartType: ProductCartEventEnum;
  paymentType?: string;
}>(({ subscriptionId, checkoutType, addToCartType, paymentType }) => ({
  event: GAEventName.PORTAL_PAYMENT_INFO,
  subscription_id: subscriptionId,
  checkout_type: checkoutType,
  add_to_cart_type: addToCartType,
  payment_type: paymentType
}));

export const subscriptionShippingInfoEvent = createAnalyticsEvent<{
  subscriptionId: number;
  checkoutType: CheckoutType;
  addToCartType: ProductCartEventEnum;
  checkoutStepOption: CheckoutStepOptionEnum;
  shippingTier?: string;
}>(({ subscriptionId, checkoutType, checkoutStepOption, addToCartType, shippingTier }) => ({
  event: GAEventName.PORTAL_SHIPPING_INFO,
  subscription_id: subscriptionId,
  add_to_cart_type: addToCartType,
  checkout_type: checkoutType,
  checkout_step_option: checkoutStepOption,
  shipping_tier: shippingTier
}));
